import { useEffect, useRef, useState } from "react";
import { ChevronDown, Check, X } from "lucide-react";

/**
 * Multi-select dropdown — visually matches the existing sidebar `<select>`s on
 * the Clothing list page (rounded, line-bordered, white bg) but lets the user
 * tick multiple options. The selected count is shown inline so the closed
 * state stays compact, and individual chips can be removed without opening
 * the panel.
 *
 * Props:
 *   options    – array of { id, label } (label can be any node)
 *   value      – array of selected `id` strings
 *   onChange   – (next: string[]) => void
 *   placeholder, testId
 */
export function MultiSelect({ options = [], value = [], onChange, placeholder = "All", testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const toggle = (id) => {
    const next = value.includes(id) ? value.filter((x) => x !== id) : [...value, id];
    onChange?.(next);
  };
  const clear = (e) => {
    e.stopPropagation();
    onChange?.([]);
  };

  const labelOf = (id) => options.find((o) => o.id === id)?.label ?? id;
  const summary = value.length === 0
    ? placeholder
    : value.length === 1
      ? labelOf(value[0])
      : `${value.length} selected`;

  return (
    <div className="relative" ref={ref} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-line bg-white text-sm hover:border-forest/40 transition-colors"
        data-testid={testId ? `${testId}-trigger` : undefined}
      >
        <span className={`truncate ${value.length === 0 ? "text-muted_text" : "text-ink"}`}>{summary}</span>
        <div className="flex items-center gap-1 shrink-0">
          {value.length > 0 && (
            <span
              role="button"
              aria-label="Clear selection"
              onClick={clear}
              className="p-0.5 text-muted_text hover:text-ink"
              data-testid={testId ? `${testId}-clear` : undefined}
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-muted_text transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 border border-line bg-white shadow-lg max-h-72 overflow-auto" data-testid={testId ? `${testId}-panel` : undefined}>
          {options.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted_text">No options</div>
          ) : (
            <ul className="py-1">
              {options.map((o) => {
                const active = value.includes(o.id);
                const rowBg = o.className || "hover:bg-sand/60";
                return (
                  <li key={o.id}>
                    <button
                      type="button"
                      onClick={() => toggle(o.id)}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm text-left transition-colors ${rowBg}`}
                      data-testid={testId ? `${testId}-option-${o.id}` : undefined}
                    >
                      <span className="truncate">{o.label}</span>
                      {active && <Check className="w-3.5 h-3.5 text-forest shrink-0" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
